import { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { patientService } from '../services/patientService'
import { immunisationService } from '../services/immunisationService'
import { Patient } from '../types/patient'
import { Immunisation } from '../types/immunisation'
import './PatientImmunisationsPrint.css'

export function PatientImmunisationsPrint() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const {
    data: patient,
    isLoading: loadingPatient,
    error: patientError,
  } = useQuery<Patient>({
    queryKey: ['patient', id],
    queryFn: () => patientService.getById(id!),
    enabled: !!id,
  })

  const {
    data: immunisationsResponse,
    isLoading: loadingImmunisations,
    error: immunisationsError,
  } = useQuery<{ items: Immunisation[]; totalCount: number; hasMore: boolean }>({
    queryKey: ['patient-immunisations', id, 'print'],
    queryFn: () => immunisationService.getPatientImmunisations(id!),
    enabled: !!id,
  })

  const isLoading = loadingPatient || loadingImmunisations
  const error = patientError || immunisationsError

  useEffect(() => {
    // Optionally auto-open print dialog once loaded
    if (!isLoading && !error) {
      // Small timeout so layout can render
      const t = setTimeout(() => {
        window.print()
      }, 300)
      return () => clearTimeout(t)
    }
  }, [isLoading, error])

  if (!id) {
    return <div className="print-wrapper">Invalid patient.</div>
  }

  if (isLoading) {
    return <div className="print-wrapper">Preparing immunisation history...</div>
  }

  if (error || !patient || !immunisationsResponse) {
    return <div className="print-wrapper">Unable to load immunisation history for printing.</div>
  }

  const immunisations = immunisationsResponse.items || []
  const totalCount = immunisationsResponse.totalCount || 0
  const visibleCount = immunisations.length
  const hasOverflow = totalCount > visibleCount

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    } catch {
      return dateString
    }
  }

  return (
    <div className="immunisations-print-page">
      <div className="print-toolbar no-print">
        <button className="btn-secondary" onClick={() => navigate(-1)}>
          Back
        </button>
        <button className="btn-primary" onClick={() => window.print()}>
          Print
        </button>
      </div>

      <header className="print-header">
        <h1>Immunisation History</h1>
        <div className="patient-block">
          <div>
            <strong>
              {patient.firstName} {patient.lastName}
            </strong>
          </div>
          <div className="patient-line">
            <span>
              DOB:{' '}
              {new Date(patient.dateOfBirth).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
              })}
            </span>
            <span>Patient ID: {patient.patientNumber || patient.id.slice(0, 8)}</span>
          </div>
        </div>
        <div className="meta-line">
          <span>
            Generated:{' '}
            {new Date().toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'long',
              year: 'numeric',
            })}{' '}
            at{' '}
            {new Date().toLocaleTimeString('en-GB', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </div>
        {hasOverflow && (
          <div className="overflow-warning">
            This list may be truncated. Only {visibleCount} of {totalCount} immunisations are loaded.
          </div>
        )}
      </header>

      <section className="print-section">
        <h2>Immunisation Records</h2>
        {immunisations.length === 0 ? (
          <p className="muted">No immunisations recorded.</p>
        ) : (
          <table className="print-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Vaccine Type</th>
                <th>Batch Number</th>
                <th>Manufacturer</th>
                <th>Expiry Date</th>
                <th>Site Given</th>
                <th>Method</th>
                <th>Dose</th>
                <th>Administered By</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {immunisations.map((imm) => (
                <tr key={imm.immunisationId}>
                  <td>{formatDate(imm.date)}</td>
                  <td>
                    <strong>{imm.type}</strong>
                    {imm.recommendedByGuideline && imm.guidelineReference && (
                      <div className="guideline-ref">NIAC: {imm.guidelineReference}</div>
                    )}
                  </td>
                  <td>{imm.batchNumber}</td>
                  <td>{imm.manufacturer}</td>
                  <td>
                    {formatDate(imm.expiryDate)}
                    {imm.expiredVaccineUsed && (
                      <div className="expired-badge">⚠️ Expired at administration</div>
                    )}
                  </td>
                  <td>{imm.siteGiven || '-'}</td>
                  <td>{imm.methodOfAdmin}</td>
                  <td>
                    {imm.dose.value} {imm.dose.unit}
                  </td>
                  <td>{imm.administeredByName || '-'}</td>
                  <td>
                    {imm.notes || '-'}
                    {imm.adverseReactionWarnings && imm.adverseReactionWarnings.length > 0 && (
                      <div className="allergy-warning-note">
                        ⚠️ Allergy warnings: {imm.adverseReactionWarnings.join('; ')}
                      </div>
                    )}
                    {!imm.consentGiven && (
                      <div className="objection-note">
                        ❌ Objection: {imm.objectionReason || 'No reason provided'}
                      </div>
                    )}
                    {imm.consentGiven && imm.consentType && (
                      <div className="consent-note">
                        ✓ Consent: {imm.consentType} ({formatDate(imm.consentDate || imm.date)})
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {hasOverflow && (
        <footer className="print-footer">
          <p>
            Note: Additional immunisations not shown here may exist in the electronic record. Refer to
            the system for the complete list.
          </p>
        </footer>
      )}
    </div>
  )
}
